/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// RUN 7: Body List & Bullet Styling
// ═══════════════════════════════════════════════════════════════════════════

import { safeAlert, reportResult, RunDetail, ListGroupFlagger, ListGroupItem, applydefaultparagraphformat } from '../runs/common';
import {
  shouldExclude, isNumberedHeading,
  isAnyBodyListStyle, isAnyTableListStyle,
  detectBullet, detectNumberedList, detectAlphaList,
  getBulletLevel, getIndentLevel,
  classifyListItem,
  getTargetStyle,
} from '../runs/listDetection';

// ── Per-run mutable counters bundled into one object for clarity ──
interface RunCounters {
  totalChecked: number;
  existingStyled: number;
  patternsConverted: number;
  skippedCount: number;
  wrongContextFixed: number;
  headersDemoted: number;
}

// FIX S4323: Replace repeated inline union type with a single type alias
type ListType = "bullet" | "number" | "alpha";

type PhaseResult =
  | "continue"
  | "handled"
  | "skip-empty"
  | "skip-excluded"
  | "skip-in-table"
  | "skip-already-correct";

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 1 — Universal skip checks
// ═══════════════════════════════════════════════════════════════════════════

function phase1_universalSkips(
  paraText: string,
  paraStyle: string,
  styleBI: string
): PhaseResult {
  if (!paraText.trim()) return "skip-empty";
  if (shouldExclude(paraStyle)) return "skip-excluded";
  if (styleBI && /^Heading[1-9]$/.test(styleBI.replace(/\s/g, ''))) return "skip-excluded";
  if (isNumberedHeading(paraText)) return "skip-excluded";
  return "continue";
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 1b — Skip paragraphs sitting inside tables (handled by run25)
// ═══════════════════════════════════════════════════════════════════════════

async function phase1b_skipIfInTable(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  try {
    const parentTable = para.parentTableOrNullObject;
    parentTable.load("isNullObject");
    await context.sync();
    return !parentTable.isNullObject;
  } catch {
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 2 — REMOVED in FLAG mode
// ═══════════════════════════════════════════════════════════════════════════
//
// Run 7 used to demote header-shaped paragraphs (e.g. "Inclusion Criteria:")
// out of any list they ended up in. That was a write-action — `para.style =
// "Normal"` plus list detachment — and writes are exactly what flag mode
// avoids. If a real header has wrongly been styled as a list item, it will
// be caught by Phase 5 (the listOrNullObject path) and flagged with a
// comment, so the reviewer sees and decides.

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 3 — Already correct: style already a body list style
// ═══════════════════════════════════════════════════════════════════════════

function phase3_alreadyCorrect(paraStyle: string): PhaseResult {
  return isAnyBodyListStyle(paraStyle) ? "skip-already-correct" : "continue";
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 4 — Wrong context (classifier)
// ═══════════════════════════════════════════════════════════════════════════
//
// Returns details if the paragraph wears a Table:* list style — that's
// wrong in body context (should be a body list style like List Number).
// Pure style-name check, no list-detection involved.
//
// FIX: Changed `listType` default from "bullet" to `null`. Previously, if
// isAnyTableListStyle() returned true for a bare "Table" caption style
// (because the config's numberStyle was "Table"), no if/else branch would
// match it — but the function still returned a flag result because listType
// defaulted to "bullet". Now we initialise to null and bail out early
// when no branch matched, so bare "Table" paragraphs are never flagged.

async function classify_wrongContext(
  paraText: string,
  paraStyle: string
): Promise<{ listType: ListType; level: number; targetStyle: string } | null> {
  if (!isAnyTableListStyle(paraStyle)) return null;

  // FIX: Use null as sentinel — "bullet" is no longer the silent default.
  let listType: ListType | null = null;
  let level = 0;

  if (/Table:Bullet/i.test(paraStyle)) {
    listType = "bullet";
    // FIX S6594: Use RegExp.exec() instead of String.match()
    // FIX S7773: Use Number.parseInt instead of parseInt
    const m = /(\d)/.exec(paraStyle);
    level = m ? Math.max(0, Number.parseInt(m[1], 10) - 1) : 0;
  } else if (/Table:List Alpha/i.test(paraStyle)) {
    listType = "alpha";
  } else if (/Table:List Number/i.test(paraStyle)) {
    listType = "number";
  }

  // FIX: No if/else branch matched (e.g. config returned true for bare
  // "Table" style via isAnyTableListStyle but none of the Table:* regexes
  // fired). Do NOT produce a false-positive flag — return null instead.
  if (!listType) return null;

  if (listType === "bullet") level = getBulletLevel(paraText);

  const targetStyle = getTargetStyle(listType, level, false);
  return { listType, level, targetStyle };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 5 — Existing Word list (classifier)
// ═══════════════════════════════════════════════════════════════════════════
//
// Returns details if Word.js considers the paragraph part of a list AND
// the current style does not match the expected body list style.
//
// "Real list" signal: listOrNullObject non-null AND listString non-empty.
// The listString check avoids false positives from style-link auto-promoted
// plain paragraphs (which typically have an empty listString because they
// were never assigned a real position in the list).

async function classify_existingList(
  para: Word.Paragraph,
  context: Word.RequestContext,
  paraText: string,
  paraStyle: string
): Promise<{ listType: ListType; level: number; targetStyle: string; marker: string } | null> {
  const listOrNull = para.listOrNullObject;
  listOrNull.load("isNullObject");
  await context.sync();
  if (listOrNull.isNullObject) return null;

  const list = para.list;
  list.load("levelTypes");
  const listItem = para.listItem;
  listItem.load("level, listString");
  await context.sync();

  const level = listItem.level;
  const levelTypes = list.levelTypes;
  const listString = (listItem.listString || "").trim();
  const levelType = (levelTypes && levelTypes.length > level) ? levelTypes[level] : "";

  if (!listString) return null;  // not a real list item

  const listType = classifyListItem(levelType, listString);
  if (!listType) return null;

  let finalLevel = level;
  if (listType === "bullet") finalLevel = getBulletLevel(paraText);

  const targetStyle = getTargetStyle(listType, finalLevel, false);
  if (paraStyle === targetStyle) return null;

  return { listType, level: finalLevel, targetStyle, marker: listString };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 6 — Pattern detection (classifier)
// ═══════════════════════════════════════════════════════════════════════════

async function classify_patternMatch(
  para: Word.Paragraph,
  context: Word.RequestContext,
  paraText: string,
  paraStyle: string
): Promise<{ listType: ListType; level: number; targetStyle: string } | null> {
  if (isAnyBodyListStyle(paraStyle)) return null;  // already styled, skip

  const isBullet = detectBullet(paraText);
  const isAlpha = detectAlphaList(paraText);
  const isNumber = detectNumberedList(paraText);
  if (!isBullet && !isNumber && !isAlpha) return null;

  let listType: ListType;
  let level: number;
  if (isBullet) {
    listType = "bullet";
    level = getBulletLevel(paraText);
  } else if (isAlpha) {
    listType = "alpha";
    level = await getIndentLevel(para, context);
  } else {
    listType = "number";
    level = await getIndentLevel(para, context);
  }

  const targetStyle = getTargetStyle(listType, level, false);
  return { listType, level, targetStyle };
}

// ═══════════════════════════════════════════════════════════════════════════
// Per-paragraph classifier — produces a flag descriptor or "skip"
// ═══════════════════════════════════════════════════════════════════════════

interface ClassifyResult {
  kind: "skip" | "flag";
  item?: ListGroupItem;
}

async function classifyBodyParagraph(
  para: Word.Paragraph,
  context: Word.RequestContext,
  loc: string,
  counters: RunCounters
): Promise<ClassifyResult> {
  para.load("text, style, styleBuiltIn, uniqueLocalId");
  await context.sync();

  const paraText = para.text || "";
  const paraStyle = para.style || "";
  const styleBI = para.styleBuiltIn || "";

  counters.totalChecked++;

  // Phase 1 — universal skips
  const r1 = phase1_universalSkips(paraText, paraStyle, styleBI);
  if (r1 === "skip-empty") return { kind: "skip" };
  if (r1 === "skip-excluded") { counters.skippedCount++; return { kind: "skip" }; }

  // Phase 1b — skip if inside a table (run25 handles those)
  if (await phase1b_skipIfInTable(para, context)) {
    counters.skippedCount++;
    return { kind: "skip" };
  }

  // Phase 3 — already correct
  if (phase3_alreadyCorrect(paraStyle) === "skip-already-correct")
    { await applydefaultparagraphformat(para, paraStyle, context);
      return {kind: "skip" }};

  // Phase 4 — wrong context (Table:* style in body)
  const wc = await classify_wrongContext(paraText, paraStyle);
  if (wc) {
    console.log(`${loc} WRONG-CTX-FLAG | "${paraStyle}" should be "${wc.targetStyle}"`);
    counters.wrongContextFixed++;
    return {
      kind: "flag",
      item: {
        kind: `wrong-ctx:${wc.listType}`,
        para, paraId: para.uniqueLocalId, paraText,
        currentStyle: paraStyle, expectedStyle: wc.targetStyle,
        level: wc.level, marker: "",
        contextLabel: "body",
        headerLabel: "List/bullet style mismatch (Table:* style in body context)",
      }
    };
  }

  // Phase 5 — existing Word list
  const ex = await classify_existingList(para, context, paraText, paraStyle);
  if (ex) {
    console.log(`${loc} EXISTING-${ex.listType.toUpperCase()}-FLAG | "${paraStyle}" should be "${ex.targetStyle}" L${ex.level} ls="${ex.marker}"`);
    counters.existingStyled++;
    return {
      kind: "flag",
      item: {
        kind: `existing:${ex.listType}`,
        para, paraId: para.uniqueLocalId, paraText,
        currentStyle: paraStyle, expectedStyle: ex.targetStyle,
        level: ex.level, marker: ex.marker,
        contextLabel: "body",
        headerLabel: `List/bullet style mismatch (body ${ex.listType} list)`,
      }
    };
  }

  // Phase 6 — text pattern
  const pm = await classify_patternMatch(para, context, paraText, paraStyle);
  if (pm) {
    console.log(`${loc} PATTERN-${pm.listType.toUpperCase()}-FLAG | L${pm.level} should be "${pm.targetStyle}" | "${paraText.trim().substring(0, 50)}"`);
    counters.patternsConverted++;
    return {
      kind: "flag",
      item: {
        kind: `pattern:${pm.listType}`,
        para, paraId: para.uniqueLocalId, paraText,
        currentStyle: paraStyle, expectedStyle: pm.targetStyle,
        level: pm.level, marker: "",
        contextLabel: "body",
        headerLabel: `Possible un-styled ${pm.listType} list (body)`,
        detail: "(Detected from text pattern; paragraph isn't styled as a list.)",
      }
    };
  }

  return { kind: "skip" };
}

// ═══════════════════════════════════════════════════════════════════════════
// Top-level entry point
// ═══════════════════════════════════════════════════════════════════════════

export async function run7() {
  await Word.run(async (context) => {
    try {
      // No change-tracking needed — flag mode only inserts comments and
      // writes to the QC summary. Comments are not tracked changes.

      const counters: RunCounters = {
        totalChecked: 0,
        existingStyled: 0,
        patternsConverted: 0,
        skippedCount: 0,
        wrongContextFixed: 0,
        headersDemoted: 0,
      };
      const changedParas: RunDetail[] = [];
      console.log("═".repeat(70));
      console.log("RUN 7: Body List & Bullet Styling — FLAG MODE");
      console.log("═".repeat(70));
      console.log("Mode    : FLAG ONLY — drops a comment at each mismatch and");
      console.log("          adds a clickable Go-To entry in the QC summary.");
      console.log("          Does NOT modify paragraph styles automatically.");
      console.log("Bullets : expected — List Bullet, List Bullet 2/3");
      console.log("Numbers : expected — List Number, List Number 2");
      console.log("Alpha   : expected — List Alpha, List Alpha 2");
      console.log("Scope   : Body paragraphs ONLY (tables handled by Run 25)\n");

      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();

      const total = paragraphs.items.length;
      console.log(`Body paragraphs: ${total}\n`);

      // Walk paragraphs in document order, feeding each into the flagger.
      // A non-flag paragraph closes any in-progress flag group, so adjacent
      // flag items group together but are never crossed by a clean break.
      const flagger = new ListGroupFlagger(context, changedParas);

      for (let i = 0; i < total; i++) {
        const para = paragraphs.items[i];
        const loc = `[Body ${i + 1}/${total}]`;
        const result = await classifyBodyParagraph(para, context, loc, counters);
        if (result.kind === "flag" && result.item) {
          await flagger.add(result.item);
        } else {
          await flagger.flush();
        }
      }
      // Final flush for any list ending at the end of the body.
      await flagger.flush();

      const flaggerStats = flagger.getStats();

      const totalFlaggedItems =
        counters.existingStyled +
        counters.patternsConverted +
        counters.wrongContextFixed;

      console.log("\n" + "═".repeat(70));
      console.log(`Checked                       : ${counters.totalChecked}`);
      console.log(`Skipped                       : ${counters.skippedCount}`);
      console.log(`Flagged — wrong-context       : ${counters.wrongContextFixed}`);
      console.log(`Flagged — existing list       : ${counters.existingStyled}`);
      console.log(`Flagged — pattern detected    : ${counters.patternsConverted}`);
      console.log(`TOTAL FLAGGED ITEMS           : ${totalFlaggedItems}`);
      console.log(`  → emitted as ${flaggerStats.groupsEmitted} comment(s) (one per list)`);
      console.log("✓ Body List & Bullet check complete!");
      console.log("═".repeat(70));

      if (flaggerStats.groupsEmitted > 0) {
        const itemNoun = totalFlaggedItems === 1 ? 'item' : 'items';
        const listNoun = flaggerStats.groupsEmitted === 1 ? 'list' : 'lists';
        reportResult(
          `Body List & Bullet — ${flaggerStats.groupsEmitted} ${listNoun} flagged (${totalFlaggedItems} ${itemNoun})`,
          [
            { label: 'Lists flagged with comments',       count: flaggerStats.groupsEmitted },
            { label: 'Total items inside flagged lists',  count: totalFlaggedItems },
            { label: 'Wrong-context items',               count: counters.wrongContextFixed },
            { label: 'Existing-list mismatch items',      count: counters.existingStyled },
            { label: 'Pattern-detected items',            count: counters.patternsConverted },
            ...changedParas,
          ]
        );
      } else {
        reportResult('Body List & Bullet Styling — no issues found',
          [{ label: 'All body list styles look correct', note: 'No comments added' }]);
      }
    } catch (error) {
      console.error("Error in run7:", error);
      safeAlert("Error in Body List & Bullet Styling:\n" + (error as Error).message);
    }
  });
}